import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Download, Info, Play, Trash2 } from "lucide-react";

export interface FileCardActionLabels {
  details: string;
  detailsAriaLabel?: string;
  play?: string;
  download?: string;
  downloadAriaLabel?: string;
  delete?: string;
  deleteAriaLabel?: string;
}

interface FileCardBaseProps {
  icon: LucideIcon;
  media?: ReactNode;
  filename: string;
  statusIcon: ReactNode;
  statusText: string;
  fileSizeLabel: ReactNode;
  header?: ReactNode;
  mobileDetails?: ReactNode;
  desktopMeta?: ReactNode;
  showSizeSeparator?: boolean;
  onDetails: () => void;
  onPlay?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  labels: FileCardActionLabels;
}

interface FileCardActionProps {
  size: "mobile" | "desktop";
  onDetails: () => void;
  onPlay?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  labels: FileCardActionLabels;
}

function FileCardActions({
  size,
  onDetails,
  onPlay,
  onDownload,
  onDelete,
  labels,
}: FileCardActionProps) {
  const padding = size === "mobile" ? "p-1.5" : "p-2";
  const actionButton = `${padding} text-secondary-500 transition-colors rounded-lg`;

  return (
    <div
      className={`flex items-center flex-shrink-0 ${
        size === "mobile" ? "gap-1" : "gap-2"
      }`}
    >
      <button
        onClick={onDetails}
        className={`${actionButton} hover:text-indigo-500 hover:bg-indigo-50`}
        title={labels.details}
        aria-label={labels.detailsAriaLabel}
      >
        <Info className="w-4 h-4" />
      </button>
      {onPlay && (
        <button
          onClick={onPlay}
          className={`${actionButton} text-primary-500 hover:text-primary-600 hover:bg-primary-50`}
          title={labels.play}
        >
          <Play className="w-4 h-4" />
        </button>
      )}
      {onDownload && (
        <button
          onClick={onDownload}
          className={`${actionButton} hover:text-primary-500 hover:bg-primary-50`}
          title={labels.download}
          aria-label={labels.downloadAriaLabel}
        >
          <Download className="w-4 h-4" />
        </button>
      )}
      {onDelete && (
        <button
          onClick={onDelete}
          className={`${actionButton} hover:text-red-500 hover:bg-red-50`}
          title={labels.delete}
          aria-label={labels.deleteAriaLabel}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default function FileCard({
  icon: Icon,
  media,
  filename,
  statusIcon,
  statusText,
  fileSizeLabel,
  header,
  mobileDetails,
  desktopMeta,
  showSizeSeparator = false,
  onDetails,
  onPlay,
  onDownload,
  onDelete,
  labels,
}: FileCardBaseProps) {
  return (
    <div className="p-3 sm:p-4 bg-white border border-secondary-200 rounded-lg">
      {header}
      {/* Mobile Layout: Multi-row (default, hidden on sm+) */}
      <div className="sm:hidden">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {media ?? <Icon className="w-6 h-6 text-secondary-400 flex-shrink-0" />}
            <div className="min-w-0 flex-1">
              <p className="font-medium text-secondary-900 text-sm break-words leading-tight">
                {filename}
              </p>
            </div>
          </div>

          <FileCardActions
            size="mobile"
            onDetails={onDetails}
            onPlay={onPlay}
            onDownload={onDownload}
            onDelete={onDelete}
            labels={labels}
          />
        </div>

        <div className="flex items-center gap-3 text-sm text-secondary-500 mb-1">
          <div className="flex items-center gap-1.5">
            {statusIcon}
            <span>{statusText}</span>
          </div>
          {showSizeSeparator && <span>•</span>}
          <span>{fileSizeLabel}</span>
        </div>

        {mobileDetails && (
          <div className="flex flex-col gap-2 text-sm text-secondary-500">
            {mobileDetails}
          </div>
        )}
      </div>

      {/* Desktop Layout: Single row (hidden on mobile, shown on sm+) */}
      <div className="hidden sm:flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {media ?? <Icon className="w-8 h-8 text-secondary-400 flex-shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="font-medium text-secondary-900 truncate">{filename}</p>
            <div className="flex items-center gap-2 text-sm text-secondary-500 flex-wrap">
              {statusIcon}
              <span>{statusText}</span>
              {showSizeSeparator && <span>•</span>}
              <span>{fileSizeLabel}</span>
              {desktopMeta}
            </div>
          </div>
        </div>

        <FileCardActions
          size="desktop"
          onDetails={onDetails}
          onPlay={onPlay}
          onDownload={onDownload}
          onDelete={onDelete}
          labels={labels}
        />
      </div>
    </div>
  );
}
